import React, { useEffect, useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  FormHelperText,
  Chip,
  Paper,
  Box,
  Divider,
  Checkbox,
  FormControlLabel,
  Tooltip,
  FormLabel,
} from '@material-ui/core';
import InputMask from 'react-input-mask';
import { useNotify, useRedirect } from 'react-admin';
import {
  InfoOutlined,
  AddPhotoAlternateOutlined as UploadPhotoIcon,
} from '@material-ui/icons';
import { Upload } from 'antd';

import { getBrain2Url, getProNameHeader, apiPostAsync } from '../brainApi';
import { emailValidationRegExp, filenameToExtension } from '../helper';
import SearchLocationInput from '../tabs/customer/SearchLocationInput';
import AddressFields from '../tabs/client_locations/AddressFields';
import dataProvider from '../dataProvider';

const useStyles = makeStyles(() => ({
  box: {
    display: 'flex',
    justifyContent: 'center',
  },
  form: {
    padding: '50px',
    display: 'flex',
    flexDirection: 'column',
    width: '90%',
    maxWidth: '500px',
    alignItems: 'stretch',
    '& > *': { margin: '0.5em 0' },
    '& h1': { marginTop: '0px' },
    '& h2': { marginBottom: '0px' },
    '& h4': { marginBottom: '0px' },
  },
  flex: {
    display: 'flex',
    margin: '0 -0.5em',
    '& > *': { margin: '0 0.5em', flex: 1 },
  },
}));

const DefaultBusinessState = {
  business_name: '',
  pro_name: '',
  business_phone: '',
  address: undefined,
  // {
  //   address_line_1: '',
  //   address_line_2: '',
  //   city: '',
  //   state: '',
  //   postal_code: '',
  //   country_code: '',
  // },
  logo_url: '',
  shop: false,
  industries: [],
};

const DefaultUserState = {
  first_name: '',
  last_name: '',
  email: '',
  user_phone: '',
};

const ToolTipText = {
  business_name:
    "Business Name will be displayed on the Booking Site, and visible to the Pro's customers.",
  pro_name:
    "This must be a unique URL, and will be visible to the Pro's customers.",
  industries:
    'The selected industries will be used to generate default schedule types and services.',
  business_phone:
    'This is the customer facing business phone number. A number with the same area code will be purchased for use with Zipchat if available. Format as 555-555-5555.',
  address:
    'This address is used to determine the default service area. Not yet implemented.',
  logo_url: 'This is the image used for the logo accross application and email',
  shop: 'Check the box ONLY if Pro offers in-shop services',
  first_name: 'First name of User',
  last_name: 'Last name of User',
  email: 'Used as Username of User. Also used for communication.',
  user_phone:
    'This phone number is used for the new User\'s "Customer" as well as for sending the welcome text to the User. Format as 555-555-5555.',
};

// eslint-disable-next-line import/prefer-default-export
export const ProSignup = () => {
  const [businessData, setBusinessData] = useState(DefaultBusinessState);
  const [businessDataErrors, setBusinessDataErrors] = useState({});
  const [userData, setUserData] = useState(DefaultUserState);
  const [userDataErrors, setUserDataErrors] = useState({});
  const [phoneSameAsBusiness, setPhoneSameAsBusiness] = useState(true);
  const [hasEditedProName, setHasEditedProName] = useState(false);
  const [allIndustries, setAllIndustries] = useState([]);

  const [saving, setSaving] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  const classes = useStyles();
  const notify = useNotify();
  const redirect = useRedirect();

  const fetchIndustries = () => {
    dataProvider
      .getList('industry', {
        filter: {},
        sort: { field: 'id', order: 'ASC' },
        pagination: { page: 1, perPage: 500 },
      })
      .then((response) => {
        setAllIndustries(response.data);
      });
  };

  useEffect(() => {
    fetchIndustries();
  }, []);

  const submit = async (e) => {
    setSaving(true);
    e.preventDefault();
    const validationErrors = [
      ...Object.keys(businessData).map((field) => validateBusinessField(field)),
      ...Object.keys(userData).map((field) => validateUserField(field)),
    ];
    if (validationErrors.filter((err) => !!err).length > 0) {
      notify(
        'The form has validation errors, please correct and resubmit',
        'warning',
      );
      setSaving(false);
      return;
    }
    const payload = { business_data: businessData, user_data: userData };
    const resp = await apiPostAsync('/pro', payload);
    if (resp?.pro?.id) {
      notify(`Success! Pro has been created: ${businessData.business_name}`);
      setTimeout(() => {
        redirect('/pro-list');
      }, 1000);
    } else {
      notify('Sorry, there was an error.', 'warning');
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!phoneSameAsBusiness) return;
    setUserData({
      ...userData,
      user_phone: businessData.business_phone,
    });
  }, [businessData.business_phone, phoneSameAsBusiness]);

  const handleBusinessDataChange = (e) => {
    let newValue =
      e.target.type === 'checkbox' ? e.target.checked : e.target.value;

    if (e.target.name === 'pro_name') {
      setHasEditedProName(true);
      newValue = newValue.toLowerCase().replace(/[^0-9a-z-_]/g, '');
    }

    if (e.target.type === 'tel') newValue = newValue.replace(/[^0-9]/g, '');

    if (e.target.name === 'business_name' && !hasEditedProName) {
      const proName = newValue
        .toLowerCase()
        .replaceAll(' ', '-') // replace spaces with dashes
        .replace(/[^0-9a-z-]/g, '') // remove anything that isn't a number, letter, or dash
        .replace(/-+/g, '-') // replace any number of repeating dashes with a single dash
        .replace(/^-/, '') // remove the first character if it's a dash
        .replace(/-$/, ''); // remove the last character if it's a dash
      setBusinessData({
        ...businessData,
        ...{ business_name: newValue },
        ...{ pro_name: proName },
      });
      return;
    }

    if (e.target.type === 'st_installer_id')
      newValue = newValue.replace(/[^0-9]/g, '');

    setBusinessData({
      ...businessData,
      ...{ [e.target.name]: newValue },
    });
  };

  const handleUserDataChange = (e) => {
    let newValue = e.target.value;
    if (e.target.type === 'tel') newValue = newValue.replace(/[^0-9]/g, '');
    setUserData({
      ...userData,
      ...{ [e.target.name]: newValue },
    });
  };

  const validateBusinessName = () => {
    if (businessData.business_name.length < 3)
      return 'Must have at least 3 characters';
    return '';
  };

  const validateProName = () => {
    if (businessData.pro_name.length < 3)
      return 'Must have at least 3 characters';
    return '';
  };

  const validateEmail = () => {
    if (!userData.email.match(emailValidationRegExp()))
      return 'Email is Invalid';
    return '';
  };

  const validateStInstallerID = () => {
    if (!/^\d*$/.test(businessData.st_installer_id))
      return 'Only positive whole numbers are allowed';
    return '';
  };

  const validatePostalCode = () => {
    if (
      isNaN(businessData?.postalCode) ||
      businessData?.postalCode.length !== 5
    )
      return 'Postal Code is Invalid';
    return '';
  };

  const validateBusinessPhone = () => {
    if (
      isNaN(businessData?.business_phone) ||
      businessData?.business_phone.length !== 10
    )
      return 'Please enter a valid phone number';
    return '';
  };

  const validateUserPhone = () => {
    if (isNaN(userData?.user_phone) || userData?.user_phone.length !== 10)
      return 'Please enter a valid phone number';
    return '';
  };

  const validateIndustries = () => {
    if (businessData.industries.length < 1) {
      return 'Please select at least one industry';
    }
    const vehicleIndustries = [
      'vehicle_tire_services',
      'vehicle_cleaning_detailing',
      'motorcycle_repair',
      'atv_repair',
      'plane_repair',
      'boat_repair',
      'rv_repair',
      'heavy_duty_vehicle_repair',
      'vehicle_glass_repair',
      'vehicle_brake_services',
      'vehicle_oil_change_services',
      'passenger_vehicle_repair',
    ];
    let hasVehicleIndustry = false;
    let hasHomeServiceIndustry = false;
    businessData.industries.forEach((i) => {
      if (vehicleIndustries.includes(i)) {
        hasVehicleIndustry = true;
      } else {
        hasHomeServiceIndustry = true;
      }
    });
    if (hasVehicleIndustry && hasHomeServiceIndustry) {
      return 'Please select only vehicle-related industries or only non-vehicle-related industries.';
    }
    return '';
  };

  const validateBusinessField = (fieldName) => {
    let errorMsg = '';
    switch (fieldName) {
      case 'business_name':
        errorMsg = validateBusinessName();
        break;
      case 'pro_name':
        errorMsg = validateProName();
        break;
      case 'address':
        break;
      case 'business_phone':
        errorMsg = validateBusinessPhone();
        break;
      case 'st_installer_id':
        errorMsg = validateStInstallerID();
        break;
      case 'industries':
        errorMsg = validateIndustries();
        break;
      default:
        errorMsg = '';
    }
    setBusinessDataErrors((prevState) => ({
      ...prevState,
      ...{ [fieldName]: errorMsg },
    }));
    return errorMsg;
  };

  const validateUserField = (fieldName) => {
    let errorMsg = '';
    switch (fieldName) {
      case 'email':
        errorMsg = validateEmail();
        break;
      case 'user_phone':
        errorMsg = validateUserPhone();
        break;
      default:
        errorMsg = '';
    }
    setUserDataErrors((prevState) => ({
      ...prevState,
      ...{ [fieldName]: errorMsg },
    }));
    return errorMsg;
  };

  const renderBusinessDataField = (name, label, endAdornment = null) => (
    <TextField
      label={label}
      variant="outlined"
      name={name}
      value={businessData[name] ?? (name === 'st_installer_id' ? 0 : '')}
      onChange={handleBusinessDataChange}
      error={!!businessDataErrors[name]}
      helperText={businessDataErrors[name]}
      required={name !== 'st_installer_id'}
      disabled={false}
      type="text"
      InputProps={{
        endAdornment: (
          <>
            {endAdornment}{' '}
            <Tooltip placement="top-start" title='Simpletire Installation Center ID'>
              <InfoOutlined />
            </Tooltip>
          </>
        ),
      }}
    />
  );

  const renderPhoneField = (name, label, isBusinessData) => {
    const value = isBusinessData ? businessData[name] : userData[name];
    const errors = isBusinessData
      ? businessDataErrors[name]
      : userDataErrors[name];
    const onChange = isBusinessData
      ? handleBusinessDataChange
      : handleUserDataChange;
    const disabled = isBusinessData ? false : phoneSameAsBusiness;

    return (
      <InputMask
        mask="999-999-9999"
        value={value}
        onChange={onChange}
        disabled={disabled}
        maskChar={null}
      >
        {() => (
          <TextField
            label={label}
            variant="outlined"
            name={name}
            value={value}
            error={!!errors}
            helperText={errors}
            required
            disabled={disabled}
            type="tel"
            InputProps={{
              endAdornment: (
                <Tooltip placement="top-start" title={ToolTipText[name] || ''}>
                  <InfoOutlined />
                </Tooltip>
              ),
            }}
          />
        )}
      </InputMask>
    );
  };

  const renderUserDataField = (name, label, endAdornment = null) => (
    <TextField
      label={label}
      variant="outlined"
      name={name}
      value={userData[name]}
      onChange={handleUserDataChange}
      error={!!userDataErrors[name]}
      helperText={userDataErrors[name]}
      required
      disabled={false}
      type="text"
      InputProps={{
        endAdornment: (
          <>
            {endAdornment}{' '}
            <Tooltip placement="top-start" title={ToolTipText[name] || ''}>
              <InfoOutlined />
            </Tooltip>
          </>
        ),
      }}
    />
  );

  const signingUrl = `${getBrain2Url()}/util/s3-sign/booking-app`;

  const handleImage = async (file) => {
    if (!file) {
      // User tried to upload a file that's not an allowed image type.
      notify('Error: File type not allowed', 'warning');
      return;
    }
    setImageLoading(true);

    const extension = filenameToExtension(file.name);
    const currentTimestamp = Date.now();
    const filename = `${currentTimestamp}-${file.name}`;

    // NOTE: We don't yet have a pro-name for the new pro, but we aren't reading or writing to the DB from this endpoint so we are just using whatever pro_name is for the logged in user
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Pro-Name': getProNameHeader(),
    };
    // Step 1: Call Brain2 to get a signed S3 URL
    try {
      const s3SignResp = await fetch(
        `${signingUrl}?objectName=${filename}&contentType=image/*`,
        { headers },
      );
      const { signedUrl } = await s3SignResp.json();
      if (signedUrl) {
        // Step 2: Use the signed URL to upload the image to S3
        const data = await fetch(signedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': `image/${extension}` },
          body: file,
        });
        setImageLoading(false);
        if (data.status === 200) {
          // Step 3: Put S3 image url into state to send to brain2
          const url = `https://zippityinfra.imgix.net/booking-app/${filename}?auto=format,compress`;
          setBusinessData({
            ...businessData,
            ...{ logo_url: url },
          });
        } else {
          console.error(data);
          notify('Error: Could not upload file1', 'warning');
        }
      } else {
        setImageLoading(false);
        console.error(s3SignResp);
        notify('Error: Could not upload file2', 'warning');
      }
    } catch (err) {
      setImageLoading(false);
      console.error(err);
      notify('Error: Could not upload file3', 'warning');
    }
  };

  const setAddressWithValidation = (addressValues) => {
    setBusinessData((prevState) => ({
      ...prevState,
      ...{ address: addressValues },
    }));
  };

  // TODO: Remove individual address fields?
  const handleChangeField = (event) => {
    const fieldObject = { [event.target.id]: event.target.value };
    const newAddress = {
      ...businessData.address,
      ...fieldObject,
    };
    setBusinessData({
      ...businessData,
      ...{ address: newAddress },
    });
  };

  const uploadButton = (
    <div>
      {imageLoading ? <CircularProgress /> : <UploadPhotoIcon />}
      <div style={{ marginTop: 8 }}>Add Logo</div>
    </div>
  );

  const bookingSuffix =
    process.env.REACT_APP_DATA_PROVIDER === 'prod'
      ? '.book.zippityinfra.com'
      : '.staging-book.zippityinfra.com';

  return (
    <Box className={classes.box}>
      <Paper
        component="form"
        autoComplete="off"
        className={classes.form}
        onSubmit={submit}
      >
        <div
          style={{
            display: 'flex',
            flex: 1,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h1>Instant Signup</h1>
          <div>
            <Upload
              name="image"
              accept="image/*"
              fileList={[]}
              maxCount={1}
              beforeUpload={(file) => handleImage(file)}
              // dummy request to bypass making a POST request
              customRequest={({ onSuccess }) =>
                setTimeout(() => onSuccess('ok'), 0)
              }
              listType="picture-card"
            >
              {businessData.logo_url ? (
                <img
                  src={businessData.logo_url}
                  alt="avatar"
                  style={{ height: '100px' }}
                />
              ) : (
                uploadButton
              )}
            </Upload>
          </div>
        </div>
        <h2>Business Information</h2>
        <Divider />
        {renderBusinessDataField('business_name', 'Business Name')}
        {renderBusinessDataField(
          'pro_name',
          'Booking App URL',
          <span style={{ whiteSpace: 'nowrap' }}>{bookingSuffix}</span>,
        )}
        {renderPhoneField('business_phone', 'Business Phone', true)}
        <SearchLocationInput
          setAddress={setAddressWithValidation}
          variant="outlined"
          isRequired
          helperText={businessDataErrors.address || ''}
        />
        <AddressFields
          address={businessData.address}
          handleChangeField={handleChangeField}
          variant="filled"
          addressErrors={businessDataErrors.address || []}
        />
        {renderBusinessDataField('st_installer_id', 'ST Installer ID')}
        <FormControlLabel
          checked={businessData.shop}
          control={<Checkbox />}
          label={
            <>
              Shop Services
              <Tooltip placement="top-start" title={ToolTipText.shop}>
                <InfoOutlined />
              </Tooltip>
            </>
          }
          name="shop"
          onChange={handleBusinessDataChange}
        />
        <Box>
          <FormLabel
            component="legend"
            style={{ display: 'contents' }}
            required
          >
            Industries
          </FormLabel>
          <Tooltip placement="top-start" title={ToolTipText.industries}>
            <InfoOutlined />
          </Tooltip>
        </Box>
        <FormControl className={classes.input} variant="outlined" required>
          <Select
            value={businessData.industries}
            labelWidth={0}
            variant="outlined"
            style={{ minWidth: '100px' }}
            name="industries"
            onChange={handleBusinessDataChange}
            renderValue={() =>
              businessData.industries.map((val) => {
                const label = allIndustries.find((i) => i.internal_name === val)
                  ?.external_name;
                return <Chip color="secondary" key={val} label={label} />;
              })
            }
            multiple
            error={!!businessDataErrors?.industries}
          >
            {allIndustries.map(({ internal_name, external_name }) => (
              <MenuItem key={internal_name} value={internal_name}>
                {external_name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{businessDataErrors?.industries}</FormHelperText>
        </FormControl>

        <h2>User Information</h2>
        <Divider />
        <div className={classes.flex}>
          {renderUserDataField('first_name', 'First Name')}
          {renderUserDataField('last_name', 'Last Name')}
        </div>
        {/* <div className={classes.flex}> */}
        {renderUserDataField('email', 'Email')}
        <FormControlLabel
          checked={phoneSameAsBusiness}
          control={<Checkbox />}
          label="Phone same as business"
          onChange={(e) => setPhoneSameAsBusiness(e.target.checked)}
        />
        {renderPhoneField('user_phone', 'Phone', false)}
        {/* </div> */}
        <Button
          disabled={saving}
          color="primary"
          variant="contained"
          size="large"
          type="submit"
        >
          {saving && (
            <CircularProgress
              className={classes.icon}
              size={18}
              thickness={2}
            />
          )}
          Submit
        </Button>
      </Paper>
    </Box>
  );
};
